import React, { useState, useEffect, useRef } from 'react'
import { ConnectWebSocketForOverlay, SetSocketMessageHandler, ClearSocketMessageHandler, DisconnectWebSocket } from './../../utilities/BL/Zones'
import { GetBongoConfig } from './../../utilities/BL/Bongo'
import { Bongo as BongoOverlay } from './Overlays/Bongo'

/**
 * COMPONENTE: CONTENEDOR BONGO CAM OVERLAY (ROUTE)
 * ------------------------------------------------------------------
 * 1. Establece la conexión SignalR.
 * 2. Maneja el bucle automático a BPM y reactivo a audio (Micrófono).
 * 3. Registra atajos físicos locales en el OBS.
 * ------------------------------------------------------------------
 */
const Bongo = () => {
    const [config, setConfig] = useState({
        enabled: true,
        visible: true,
        bongoSkin: 'classic-wood',
        hitEffect: 'shockwave',
        hitColor: '#00ffff',
        bgPreset: 'tropical-sunset',
        decoStyle: 'beach-tropical',
        floatingShape: 'fireflies',
        shapeOpacity: 0.3,
        shapeGlow: 15,
        scale: 1.0,
        offsetX: 0,
        offsetY: 0,
        bpm: 140,
        audioReact: false
    });

    const [connectionStatus, setConnectionStatus] = useState('idle');

    // Estados visuales de golpe en vivo
    const [leftHit, setLeftHit] = useState(false);
    const [rightHit, setRightHit] = useState(false);
    const [intensityLevel, setIntensityLevel] = useState(0); // 0 a 4
    const [comboCount, setComboCount] = useState(0);
    const [shaking, setShaking] = useState(false);

    // Refs para temporizadores y Audio React
    const comboTimeoutRef = useRef(null);
    const autoIntervalRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const streamRef = useRef(null);
    const audioIntervalRef = useRef(null);
    const toggleHitRef = useRef(true);

    // OBS Canvas transparente completo
    useEffect(() => {
        const originalBodyBg = document.body.style.backgroundColor;
        const originalHtmlBg = document.documentElement.style.backgroundColor;
        const originalBodyOverflow = document.body.style.overflow;
        const originalHtmlOverflow = document.documentElement.style.overflow;
        const rootElement = document.getElementById('root');

        document.body.style.backgroundColor = 'transparent';
        document.documentElement.style.backgroundColor = 'transparent';
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        if (rootElement) {
            rootElement.style.backgroundColor = 'transparent';
            rootElement.style.overflow = 'hidden';
        }

        return () => {
            document.body.style.backgroundColor = originalBodyBg;
            document.documentElement.style.backgroundColor = originalHtmlBg;
            document.body.style.overflow = originalBodyOverflow;
            document.documentElement.style.overflow = originalHtmlOverflow;
            if (rootElement) {
                rootElement.style.removeProperty('background-color');
                rootElement.style.removeProperty('overflow');
            }
        };
    }, []);

    // Conexión y sincronización WebSocket
    useEffect(() => {
        const initBongo = async () => {
            const res = await GetBongoConfig();
            if (res.success && res.data) {
                setConfig(prev => ({ ...prev, ...res.data }));
            }

            await ConnectWebSocketForOverlay(
                'bongo',
                (status) => setConnectionStatus(status),
                (msg) => console.log('[Bongo Overlay] WS status:', msg)
            );

            SetSocketMessageHandler(handleIncomingMessage);
        };

        const handleIncomingMessage = (message) => {
            if (!message || !message.type) return;

            const msgType = message.type.toLowerCase();
            const payload = message.data;

            if (msgType === 'bongo') {
                if (payload.action === 'config') {
                    setConfig(prev => ({ ...prev, ...payload.config }));
                } else if (payload.action === 'show') {
                    setConfig(prev => ({ ...prev, visible: true }));
                } else if (payload.action === 'hide') {
                    setConfig(prev => ({ ...prev, visible: false }));
                } else if (payload.action === 'hit') {
                    triggerHit(payload.side === 'left');
                } else if (payload.action === 'mode') {
                    setIntensityLevel(payload.level);
                }
            }
        };

        initBongo();

        return () => {
            ClearSocketMessageHandler();
            DisconnectWebSocket();
            stopAudioReact();
            stopAutoPlay();
        };
    }, []);

    // Encender / Apagar el AutoPlay a BPM o Audio React
    useEffect(() => {
        if (!config.enabled || !config.visible) {
            stopAutoPlay();
            stopAudioReact();
            return;
        }

        if (config.audioReact) {
            stopAutoPlay();
            startAudioReact();
        } else {
            stopAudioReact();
            startAutoPlay();
        }

        return () => {
            stopAutoPlay();
            stopAudioReact();
        };
    }, [config.audioReact, config.enabled, config.visible, config.bpm]);

    // Ejecutar un golpe de bongo (Izquierda/Derecha)
    const triggerHit = (isLeft, isManual = false) => {
        if (isLeft) {
            setLeftHit(true);
            setTimeout(() => setLeftHit(false), 80);
        } else {
            setRightHit(true);
            setTimeout(() => setRightHit(false), 80);
        }

        // Efecto Shaking (Temblores de pantalla en nivel 3 y 4)
        if (intensityLevel >= 3) {
            setShaking(true);
            setTimeout(() => setShaking(false), 120);
        }

        // Registrar Combo
        if (intensityLevel === 4 && isManual) {
            setComboCount(prev => prev + 1);
            if (comboTimeoutRef.current) clearTimeout(comboTimeoutRef.current);
            comboTimeoutRef.current = setTimeout(() => {
                setComboCount(0);
            }, 1500);
        }
    };

    // Bucle automático a tempo (BPM)
    const startAutoPlay = () => {
        stopAutoPlay();
        const intervalMs = 60000 / (config.bpm || 140);
        autoIntervalRef.current = setInterval(() => {
            triggerHit(toggleHitRef.current, false);
            toggleHitRef.current = !toggleHitRef.current;
        }, intervalMs);
    };

    const stopAutoPlay = () => {
        if (autoIntervalRef.current) {
            clearInterval(autoIntervalRef.current);
            autoIntervalRef.current = null;
        }
    };

    // Bucle de micrófono Audio React
    const startAudioReact = async () => {
        stopAudioReact();
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            const AudioContext = window.AudioContext || window.webkitAudioContext;
            const audioContext = new AudioContext();
            audioContextRef.current = audioContext;

            const microphone = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 256;
            analyserRef.current = analyser;

            microphone.connect(analyser);

            const bufferLength = analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);
            let lastHitTime = 0;

            const processAudio = () => {
                if (!analyserRef.current) return;
                analyserRef.current.getByteFrequencyData(dataArray);

                // Sacar frecuencias graves (kick bass)
                let bassSum = 0;
                for (let i = 0; i < 10; i++) bassSum += dataArray[i];
                let bassAvg = bassSum / 10;

                const now = Date.now();
                if (bassAvg > 200 && (now - lastHitTime > 150)) {
                    triggerHit(toggleHitRef.current, false);
                    toggleHitRef.current = !toggleHitRef.current;
                    lastHitTime = now;
                }

                audioIntervalRef.current = requestAnimationFrame(processAudio);
            };

            processAudio();
        } catch (err) {
            console.error('Mic error:', err);
        }
    };

    const stopAudioReact = () => {
        if (audioIntervalRef.current) {
            cancelAnimationFrame(audioIntervalRef.current);
            audioIntervalRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
        analyserRef.current = null;
    };

    // Teclado local para Interact OBS
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!config.enabled) return;

            if (e.key === '1' || e.key === 'ArrowLeft') {
                triggerHit(true, true);
            } else if (e.key === '2' || e.key === 'ArrowRight') {
                triggerHit(false, true);
            } else if (e.key === '3') {
                setIntensityLevel(prev => (prev + 1) % 5);
            } else if (e.code === 'Space') {
                e.preventDefault();
                setConfig(prev => ({ ...prev, visible: !prev.visible }));
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [config.enabled, intensityLevel]);

    if (!config.enabled) return null;

    return (
        <>
            <BongoOverlay 
                config={config} 
                leftHitActive={leftHit}
                rightHitActive={rightHit}
                intensityLevel={intensityLevel}
                comboCount={comboCount}
                shaking={shaking}
            />

            {connectionStatus === 'error' && (
                <div style={{ position: 'fixed', bottom: 0, left: 150, background: '#ef4444', color: 'white', padding: '4px 8px', fontSize: 10, zIndex: 99999 }}>
                    BONGO WS DISCONNECTED
                </div>
            )}
        </>
    );
};

export { Bongo };
export default Bongo;
